import {
  Box,
  TableRow,
  Paper,
  TableContainer,
  CircularProgress,
  Typography,
  Table,
  TableHead,
  TableCell,
  TableBody,
  Button,
  Breadcrumbs,
  Link,
  Chip,
  LinearProgress,
  Pagination,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import React, { useState } from "react";
import { useListCustomNotificationCampaigns } from "../../../clients/graphql/pages/notification-queries";
import moment from "moment";

interface CustomNotificationCampaign {
  notificationKey: number;
  targetMode: string;
  targetDefinition: string;
  scheduledAt: string;
  endAt: string;
  totalRecipients: number;
  sentCount: number;
  ackedCount: number;
  createdAt: string;
  subject: string;
  body: string;
}

export const CustomNotificationCampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 20;
  const offset = (page - 1) * limit;

  const { loading, error, data } = useListCustomNotificationCampaigns(
    limit,
    offset
  );

  const getStatusChip = (campaign: CustomNotificationCampaign) => {
    const now = moment();
    const scheduledAt = moment(campaign.scheduledAt);
    const endAt = moment(campaign.endAt);

    if (now.isBefore(scheduledAt)) {
      return <Chip label="Scheduled" color="info" size="small" />;
    } else if (now.isAfter(endAt)) {
      return <Chip label="Completed" color="success" size="small" />;
    } else {
      return <Chip label="In Progress" color="warning" size="small" />;
    }
  };

  const getProgressPercentage = (sent: number, total: number) => {
    return total > 0 ? Math.round((sent / total) * 100) : 0;
  };

  const getAckPercentage = (acked: number, total: number) => {
    return total > 0 ? Math.round((acked / total) * 100) : 0;
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Error loading custom notification campaigns: {error.message}
        </Typography>
      </Box>
    );
  }

  const campaigns = data?.listCustomNotificationCampaigns || [];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs>
          <Link component={RouterLink} to="/developer" color="inherit">
            Developer
          </Link>
          <Link
            component={RouterLink}
            to="/developer/notifications"
            color="inherit"
          >
            Notifications
          </Link>
          <Typography color="text.primary">Custom Campaigns</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/developer/notifications")}
          sx={{ mb: 2, p: 0, color: "text.secondary" }}
        >
          Back to Templates
        </Button>
        <Typography variant="h4">Custom Notification Campaigns</Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          These are notification campaigns that were sent without using a
          notification template.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          component={RouterLink}
          to="/developer/notifications/custom-campaigns/create"
        >
          Create Custom Campaign
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Campaign ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Target Mode</TableCell>
              <TableCell>Scheduled At</TableCell>
              <TableCell>Total Recipients</TableCell>
              <TableCell>Sent Progress</TableCell>
              <TableCell>Acknowledgment Rate</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No custom campaigns found
                </TableCell>
              </TableRow>
            ) : (
              campaigns.map((campaign: CustomNotificationCampaign) => {
                const sentPercentage = getProgressPercentage(
                  campaign.sentCount,
                  campaign.totalRecipients
                );
                const ackPercentage = getAckPercentage(
                  campaign.ackedCount,
                  campaign.totalRecipients
                );

                return (
                  <TableRow
                    key={campaign.notificationKey}
                    sx={{
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                  >
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/developer/notifications/campaigns/${campaign.notificationKey}`}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        #{campaign.notificationKey}
                      </Link>
                    </TableCell>
                    <TableCell>{getStatusChip(campaign)}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={campaign.subject}
                      >
                        {campaign.subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={campaign.targetMode}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {moment(campaign.scheduledAt).format(
                        "MMM DD, YYYY HH:mm"
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {campaign.totalRecipients.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={sentPercentage}
                          sx={{ width: 60, height: 8 }}
                        />
                        <Typography variant="body2">
                          {campaign.sentCount}/{campaign.totalRecipients} (
                          {sentPercentage}%)
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={ackPercentage}
                          color="success"
                          sx={{ width: 60, height: 8 }}
                        />
                        <Typography variant="body2">
                          {campaign.ackedCount}/{campaign.totalRecipients} (
                          {ackPercentage}%)
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        component={RouterLink}
                        to={`/developer/notifications/campaigns/${campaign.notificationKey}`}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {campaigns.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={Math.ceil(campaigns.length / limit) || 1}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};
